import React from "react";
import nextId from "react-id-generator";
import { mergeOverrides } from "baseui";
import { Overrides } from "baseui/overrides";
import { FormControlOverrides } from "baseui/form-control";

import FormControl from "../../inputs/form_control";
import Input, { InputOverrides, InputProps } from "../../inputs/input";
import PromptWithSecondaryText from "./shared_components/prompt_with_secondary_text";
import { useConstructAriaLabel } from "./use_construct_aria_label";
import { getFormControlLabel } from "./shared_components/shared_form_control_label";
import { captureError } from "../../../../../utils/capture_error";

const INPUT_AREA_NAMESPACE = "textarea-input";

export interface InputQuestionWrapperProps extends InputProps {
  label?: string;
  secondaryText?: string;
  supplementalQuestionPrompt?: string;
  optional: boolean;
  placeholder?: string;
  errorMessage?: string;
  overrides?: Overrides<InputOverrides>;
  ariaLabel?: string; // use this if the supplemental question prompt, secondary text, and label cannot be provided, such as in the case of a follow up input to an "other" option
}

const inputOverrides = (maxCharacterCount?: number): Overrides<InputOverrides> => ({
  Root: {
    style: ({ $theme }) => ({
      marginTop:
        // If we have a character counter, reduce the top margin.
        maxCharacterCount ? `-${$theme.sizing.scale300}` : `0`,
      maxWidth: "620px",
    }),
  },
});

const formControlOverrides = (): Overrides<FormControlOverrides> => ({
  Label: {
    style: () => ({
      marginBottom: "0",
    }),
  },
});

const InputQuestionWrapper = ({
  supplementalQuestionPrompt,
  secondaryText,
  label,
  optional,
  placeholder,
  errorMessage,
  overrides,
  ariaLabel,
  dataTest,
  ...inputProps
}: InputQuestionWrapperProps) => {
  const { constructAriaLabel } = useConstructAriaLabel();

  if (overrides) {
    overrides = mergeOverrides(inputOverrides(inputProps.maxCharacterCount), overrides);
  } else {
    overrides = inputOverrides(inputProps.maxCharacterCount);
  }

  const inputId = nextId(INPUT_AREA_NAMESPACE);

  if (
    !supplementalQuestionPrompt &&
    !secondaryText &&
    !label &&
    !ariaLabel &&
    !inputProps["aria-label"]
  ) {
    captureError(
      "Error! If input question wrapper does not have supplemental question prompt, secondary text, label, or aria-label, it must have an ariaLabel prop, otherwise the input is inaccessible for assistive technologies.",
    );
  }

  return (
    <div>
      {supplementalQuestionPrompt && (
        <PromptWithSecondaryText
          supplementalQuestionPrompt={supplementalQuestionPrompt}
          secondaryText={secondaryText}
          optional={optional}
          dataTest={dataTest}
        />
      )}

      <FormControl
        label={getFormControlLabel(
          supplementalQuestionPrompt,
          label,
          optional,
          secondaryText,
          dataTest,
        )}
        htmlFor={inputId}
        error={errorMessage}
        overrides={formControlOverrides()}
        dataTest={`${dataTest}-form-control`}
      >
        <Input
          currentCharacterCount={inputProps.currentCharacterCount}
          maxCharacterCount={inputProps.maxCharacterCount}
          aria-label={
            ariaLabel ??
            constructAriaLabel(
              supplementalQuestionPrompt,
              secondaryText,
              label,
              optional,
              inputProps.maxCharacterCount,
            )
          }
          id={inputId}
          placeholder={placeholder}
          dataTest={dataTest}
          {...inputProps}
          error={!!errorMessage}
          overrides={overrides}
        />
      </FormControl>
    </div>
  );
};

export default InputQuestionWrapper;
